import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import {
  getAllNotifications,
  markAllReadNotifications,
  markReadNotification,
  setCurrentPage,
  setPageSize,
} from 'redux/notificationSlice';
import { toast } from 'react-toastify';
import {
  FaBell, FaCheckDouble, FaSearch, FaTimes,
  FaRegEnvelope, FaEnvelopeOpen,
} from 'react-icons/fa';
import { MdFilterList } from 'react-icons/md';
import createMarkup   from 'helpers/createMarkup';
import DateTimeToText from 'helpers/dateTimetoText';
import Pagination     from 'components/common/Pagination';
import { EmployeeAvatar } from 'components/common/select/EmployeeBadge';

// ── Skeleton row ──────────────────────────────────────────────────────────────
const SkeletonRow = () => (
  <div className="tw-flex tw-items-start tw-gap-3 sm:tw-gap-4 tw-px-4 sm:tw-px-6 tw-py-4 tw-border-b tw-border-slate-100">
    <div className="tw-w-10 tw-h-10 tw-rounded-xl tw-bg-slate-100 tw-animate-pulse tw-shrink-0" />
    <div className="tw-flex-1 tw-flex tw-flex-col tw-gap-2.5 tw-pt-1">
      <div className="tw-h-3.5 tw-bg-slate-100 tw-rounded tw-animate-pulse tw-w-2/3" />
      <div className="tw-h-3 tw-bg-slate-100 tw-rounded tw-animate-pulse tw-w-5/6" />
      <div className="tw-h-2.5 tw-bg-slate-100 tw-rounded tw-animate-pulse tw-w-1/4" />
    </div>
  </div>
);

// ── Notification row ──────────────────────────────────────────────────────────
const NotificationItem = ({ notification, onRead }) => {
  const nav      = useNavigate();
  const isUnread = notification.unread;

  const handleClick = async () => {
    if (isUnread) {
      try { await onRead(notification.id); } catch { /* ignore */ }
    }
    if (notification.url) {
      try {
        const parsed = new URL(notification.url);
        nav(parsed.pathname + parsed.search);
      } catch {
        nav(notification.url);
      }
    }
  };

  return (
    <div
      onClick={handleClick}
      className={`tw-flex tw-items-start tw-gap-3 sm:tw-gap-4 tw-px-4 sm:tw-px-6 tw-py-4 tw-border-b tw-border-slate-100 tw-transition-all ${
        isUnread
          ? 'tw-bg-blue-50/40 hover:tw-bg-blue-50/70 tw-border-l-[3px] tw-border-l-blue-500 tw-cursor-pointer'
          : `tw-bg-white hover:tw-bg-slate-50/60 tw-border-l-[3px] tw-border-l-transparent ${notification.url ? 'tw-cursor-pointer' : ''}`
      }`}
    >
      {/* Avatar + dot */}
      <div className="tw-relative tw-shrink-0 tw-mt-0.5">
        <EmployeeAvatar name={notification.avatar?.name || '?'} avatarUrl={notification.avatar?.avatar_url} size={40} fontSize={14} />
        {isUnread && (
          <span className="tw-absolute -tw-top-0.5 -tw-right-0.5 tw-w-3 tw-h-3 tw-rounded-xl tw-bg-blue-500 tw-border-2 tw-border-white" />
        )}
      </div>

      {/* Body */}
      <div className="tw-flex-1 tw-min-w-0">
        <p
          className={`tw-text-sm tw-leading-relaxed tw-mb-1.5 notification-msg ${isUnread ? 'tw-text-slate-900 tw-font-medium' : 'tw-text-slate-600'}`}
          dangerouslySetInnerHTML={createMarkup(notification.message)}
        />
        <div className="tw-flex tw-items-center tw-gap-2">
          {notification.created_by && (
            <span className="tw-text-[11px] tw-font-semibold tw-text-slate-500">{notification.created_by}</span>
          )}
          {notification.created_by && <span className="tw-text-slate-200">·</span>}
          <span className="tw-text-[11px] tw-text-slate-400">
            <DateTimeToText datetime={notification.created_at} />
          </span>
        </div>
      </div>

      {/* Status */}
      <div className="tw-shrink-0 tw-flex tw-flex-col tw-items-end tw-gap-2 tw-mt-0.5">
        {isUnread ? (
          <span className="tw-inline-flex tw-items-center tw-gap-1 tw-text-[10px] tw-font-bold tw-tracking-wider tw-text-blue-700 tw-bg-blue-100 tw-border tw-border-blue-200 tw-px-2 tw-py-0.5 tw-rounded-xl">
            NEW
          </span>
        ) : (
          <FaEnvelopeOpen size={12} className="tw-text-slate-300" />
        )}
      </div>
    </div>
  );
};
NotificationItem.propTypes = {
  notification: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    unread: PropTypes.bool,
    url: PropTypes.string,
    avatar: PropTypes.shape({
      name: PropTypes.string,
      avatar_url: PropTypes.string,
    }),
    message: PropTypes.string,
    created_by: PropTypes.string,
    created_at: PropTypes.string,
  }).isRequired,
  onRead: PropTypes.func.isRequired,
};

// ── Filter stat card ──────────────────────────────────────────────────────────
const StatCard = ({ icon: Icon, label, value, color }) => (
  <div className={`tw-flex tw-items-center tw-gap-2 sm:tw-gap-3 tw-px-2 sm:tw-px-4 tw-py-3 tw-rounded-lg tw-border tw-bg-white tw-min-w-0 ${color}`}>
    <div className={`tw-w-8 tw-h-8 sm:tw-w-9 sm:tw-h-9 tw-rounded tw-flex tw-items-center tw-justify-center tw-shrink-0 ${color.replace('tw-border-', 'tw-bg-').replace('-200', '-100')}`}>
      <Icon size={15} className={color.includes('blue') ? 'tw-text-blue-600' : color.includes('emerald') ? 'tw-text-emerald-600' : 'tw-text-slate-500'} />
    </div>
    <div className="tw-min-w-0">
      <p className="tw-text-lg sm:tw-text-[22px] tw-font-bold tw-text-slate-800 tw-mb-0 tw-leading-tight tw-truncate">{value}</p>
      <p className="tw-text-[11px] tw-text-slate-400 tw-mb-0 tw-font-medium tw-truncate">{label}</p>
    </div>
  </div>
);
StatCard.propTypes = {
  icon: PropTypes.elementType.isRequired,
  label: PropTypes.node,
  value: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  color: PropTypes.string.isRequired,
};

// ── Main ──────────────────────────────────────────────────────────────────────
const PAGE_SIZE_OPTIONS = [10, 20, 50];

const NotificationList = () => {
  const dispatch = useDispatch();
  const {
    data, totalRows, totalPagesCount,
    currentPage, pageSize,
    fromRecord, toRecord,
    loading, readCount, unReadCount,
  } = useSelector(s => s.notification);

  const [search,      setSearch]      = useState('');
  const [unreadOnly,  setUnreadOnly]  = useState(false);
  const [markAllDone, setMarkAllDone] = useState(false);

  // Fetch from backend — no q param (Noticed stores GlobalID refs, not text)
  useEffect(() => {
    dispatch(getAllNotifications({
      page:     currentPage,
      per_page: pageSize,
      unread:   unreadOnly || undefined,
    }));
  }, [currentPage, pageSize, markAllDone, unreadOnly, dispatch]);

  const handleSearch = (val) => setSearch(val);

  // Client-side filter: strip HTML then case-insensitive match
  const q = search.trim().toLowerCase();
  const filteredData = q
    ? (data || []).filter(n => {
        const text = (n.message || '').replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').toLowerCase();
        return text.includes(q);
      })
    : (data || []);

  const handleUnreadToggle = () => {
    const next = !unreadOnly;
    setUnreadOnly(next);
    dispatch(setCurrentPage(1));
  };

  const handleMarkAll = async () => {
    try {
      await dispatch(markAllReadNotifications()).unwrap();
      setMarkAllDone(v => !v);
      toast.success('All marked as read.');
    } catch { toast.error('Failed.'); }
  };

  const handleRead = async (id) => {
    await dispatch(markReadNotification(id)).unwrap();
  };

  const isEmpty = !loading && filteredData.length === 0;

  return (
    <div className="tw-flex tw-flex-col" style={{ height: 'calc(100dvh - 56px)', minHeight: 420 }}>

      {/* ── Page header — sticky ── */}
      <div className="tw-bg-white tw-border-b tw-border-slate-200 tw-px-4 sm:tw-px-8 tw-py-4 sm:tw-py-5 tw-shrink-0">
        <div className="tw-flex tw-flex-wrap tw-items-center tw-justify-between tw-gap-3 tw-mb-4 sm:tw-mb-5">
          <div className="tw-flex tw-items-center tw-gap-3">
            <div className="tw-w-10 tw-h-10 tw-rounded-lg tw-bg-blue-100 tw-flex tw-items-center tw-justify-center">
              <FaBell size={18} className="tw-text-blue-600" />
            </div>
            <div>
              <h1 className="tw-text-xl tw-font-bold tw-text-slate-900 tw-mb-0">Notifications</h1>
              <p className="tw-text-xs tw-text-slate-400 tw-mb-0">All system notifications and alerts</p>
            </div>
          </div>
          {unReadCount > 0 && (
            <button type="button" onClick={handleMarkAll}
              className="tw-inline-flex tw-items-center tw-gap-2 tw-px-4 tw-py-2 tw-rounded-lg tw-text-sm tw-font-semibold tw-text-blue-600 tw-bg-blue-50 hover:tw-bg-blue-100 tw-border tw-border-blue-200 tw-cursor-pointer tw-transition-colors">
              <FaCheckDouble size={12} /> Mark all as read
            </button>
          )}
        </div>

        {/* Stat cards */}
        <div className="tw-grid tw-grid-cols-3 tw-gap-2 sm:tw-gap-4 tw-mb-4 sm:tw-mb-5">
          <StatCard icon={FaBell}          label="Total"  value={totalRows}   color="tw-border-slate-200" />
          <StatCard icon={FaEnvelopeOpen}  label="Read"   value={readCount}   color="tw-border-emerald-200" />
          <StatCard icon={FaRegEnvelope}   label="Unread" value={unReadCount} color="tw-border-blue-200" />
        </div>

        {/* Search + filters */}
        <div className="tw-flex tw-flex-wrap tw-items-center tw-gap-2 sm:tw-gap-3">
          {/* Search — min-width keeps it usable once the row wraps */}
          <div className="tw-relative tw-flex-1 tw-min-w-[180px]">
            <FaSearch size={13} className="tw-absolute tw-left-3.5 tw-top-1/2 -tw-translate-y-1/2 tw-text-slate-300 tw-pointer-events-none" />
            <input
              type="text"
              value={search}
              onChange={e => handleSearch(e.target.value)}
              placeholder="Search notifications…"
              className="tw-w-full tw-pl-10 tw-pr-9 tw-py-2.5 tw-text-sm tw-border tw-border-slate-200 tw-rounded-lg tw-bg-slate-50/60 focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-blue-300 focus:tw-bg-white focus:tw-border-blue-400 tw-transition-all"
            />
            {search && (
              <button type="button" onClick={() => handleSearch('')}
                className="tw-absolute tw-right-3 tw-top-1/2 -tw-translate-y-1/2 tw-border-0 tw-bg-transparent tw-cursor-pointer tw-text-slate-300 hover:tw-text-slate-500 tw-p-0">
                <FaTimes size={11} />
              </button>
            )}
          </div>

          {/* Unread toggle */}
          <button type="button" onClick={handleUnreadToggle}
            className={`tw-inline-flex tw-items-center tw-gap-2 tw-px-4 tw-py-2.5 tw-rounded-lg tw-text-sm tw-font-semibold tw-border tw-cursor-pointer tw-transition-all tw-whitespace-nowrap ${
              unreadOnly
                ? 'tw-bg-blue-600 tw-text-white tw-border-blue-600 hover:tw-bg-blue-700'
                : 'tw-bg-white tw-text-slate-600 tw-border-slate-200 hover:tw-bg-slate-50'
            }`}>
            <MdFilterList size={15} />
            Unread only
            {unReadCount > 0 && (
              <span className={`tw-text-[10px] tw-font-bold tw-px-1.5 tw-py-0.5 tw-rounded-xl ${
                unreadOnly ? 'tw-bg-white/20 tw-text-white' : 'tw-bg-blue-50 tw-text-blue-600 tw-border tw-border-blue-200'
              }`}>
                {unReadCount}
              </span>
            )}
          </button>

          {/* Per page */}
          <select
            value={pageSize}
            onChange={e => { dispatch(setPageSize(Number(e.target.value))); dispatch(setCurrentPage(1)); }}
            className="tw-border tw-border-slate-200 tw-rounded-lg tw-px-3 tw-py-2.5 tw-text-sm tw-text-slate-600 tw-bg-white focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-blue-300 tw-cursor-pointer tw-font-medium"
          >
            {PAGE_SIZE_OPTIONS.map(n => <option key={n} value={n}>{n} per page</option>)}
          </select>
        </div>
      </div>

      {/* ── Notification list — only this part scrolls ── */}
      <div className="tw-flex-1 tw-bg-white tw-overflow-y-auto tw-min-h-0">

        {/* Skeleton */}
        {loading && !data?.length && [...Array(8)].map((_, i) => <SkeletonRow key={i} />)}

        {/* Refreshing */}
        {loading && !!data?.length && (
          <div className="tw-flex tw-items-center tw-justify-center tw-gap-2 tw-py-2.5 tw-bg-slate-50 tw-border-b tw-border-slate-100 tw-text-xs tw-text-slate-400">
            <div className="tw-w-3.5 tw-h-3.5 tw-rounded-xl tw-border-2 tw-border-slate-200 tw-border-t-blue-500 tw-animate-spin" />
            Refreshing…
          </div>
        )}

        {/* Empty */}
        {isEmpty && (
          <div className="tw-flex tw-flex-col tw-items-center tw-justify-center tw-py-24 tw-text-slate-300 tw-gap-4">
            <FaBell size={48} />
            <div className="tw-text-center">
              <p className="tw-text-base tw-font-semibold tw-text-slate-400 tw-mb-1">
                {search ? 'No results found' : unreadOnly ? 'All caught up!' : 'No notifications yet'}
              </p>
              <p className="tw-text-sm tw-text-slate-300">
                {search ? `No notifications matching "${search}"` : unreadOnly ? 'You have no unread notifications' : 'System notifications will appear here'}
              </p>
            </div>
            {(search || unreadOnly) && (
              <button type="button"
                onClick={() => { handleSearch(''); setUnreadOnly(false); }}
                className="tw-text-sm tw-text-blue-500 hover:tw-underline tw-border-0 tw-bg-transparent tw-cursor-pointer tw-font-medium">
                Clear all filters
              </button>
            )}
          </div>
        )}

        {/* Rows */}
        {filteredData.map(n => (
          <NotificationItem key={n.id} notification={n} onRead={handleRead} />
        ))}
      </div>

      {/* ── Footer pagination ── */}
      {!isEmpty && (
        <div className="tw-bg-white tw-border-t tw-border-slate-200 tw-px-4 sm:tw-px-8 tw-py-3 sm:tw-py-3.5 tw-flex tw-flex-wrap tw-items-center tw-justify-between tw-gap-2 sm:tw-gap-4 tw-shrink-0">
          <p className="tw-text-sm tw-text-slate-500 tw-mb-0">
            Showing <span className="tw-font-semibold tw-text-slate-700">{fromRecord}–{toRecord}</span> of{' '}
            <span className="tw-font-semibold tw-text-slate-700">{totalRows}</span> notifications
          </p>
          {totalPagesCount > 1 && (
            <Pagination
              currentPage={currentPage}
              totalPages={totalPagesCount}
              onPageChange={p => dispatch(setCurrentPage(p))}
            />
          )}
        </div>
      )}
    </div>
  );
};

export default NotificationList;
